// One full-screen loader for the whole app.
//
// App.jsx renders a single <ProjectLoader> that stays mounted; the route
// fallback and each screen (master plan, 360° tour) only *hold* it open until
// they are ready. Handing off between separately mounted loaders read as two
// loaders, one after the other.
import { useLayoutEffect, useRef, useSyncExternalStore } from "react";

const holds = new Map();
const listeners = new Set();
let snapshot = { active: false, progress: null };

const emit = () => {
  const progress = [...holds.values()].find((value) => Number.isFinite(value));
  snapshot = { active: holds.size > 0, progress: progress ?? null };
  listeners.forEach((listener) => listener());
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Read by the app-level loader. */
export const useLoaderState = () => useSyncExternalStore(subscribe, () => snapshot);

/**
 * Keep the app loader up while `active`, optionally reporting `progress` (0..1).
 * Layout effects, so a hand-off (route fallback -> screen) happens within one
 * commit and the loader never gets a frame to start fading in between.
 */
export const useLoaderHold = (active, progress = null) => {
  const id = useRef(null);
  if (!id.current) id.current = Symbol("loader-hold");

  useLayoutEffect(() => {
    if (!active) return undefined;
    const key = id.current;
    holds.set(key, null);
    emit();
    return () => {
      holds.delete(key);
      emit();
    };
  }, [active]);

  useLayoutEffect(() => {
    if (!active || !holds.has(id.current)) return;
    holds.set(id.current, progress);
    emit();
  }, [active, progress]);
};
